import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Clock, Link as LinkIcon, Gauge, FlaskConical, ClipboardList,
  Radio, Plus, Pencil, Trash2, Barcode, Loader2, MapPin, Move, FileText, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '@/components/StatusBadge';
import RefLink from '@/components/RefLink';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';
import ProbeFormDialog from '@/components/ProbeFormDialog';
import DatasheetPanel from '@/components/DatasheetPanel';
import { toastApiError } from '@/lib/errors';

// Route /instruments/:id, read-only detail.
// Historie = chronologický list všech kalibrací tohoto přístroje
// (match přes name+type, stejně jako totalOrders v listu).
// Tab Sondy s CRUD na probes patřící přístroji.

const TABS = [
  { id: 'overview', label: 'Přehled', icon: Gauge },
  { id: 'datasheet', label: 'Datasheet', icon: FileText },
  { id: 'probes', label: 'Sondy', icon: Radio },
  { id: 'history', label: 'Historie kalibrací', icon: Clock },
  { id: 'related', label: 'Související', icon: LinkIcon },
];

function TabButton({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
      style={{
        color: active ? 'var(--ct-accent)' : 'var(--ct-text-secondary)',
        borderBottom: active ? '2px solid var(--ct-accent)' : '2px solid transparent',
      }}
    >
      <Icon className="w-4 h-4" />
      {tab.label}
    </button>
  );
}

// Lokace měřidla
function LocationSection({ inst, onRefresh }) {
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/instruments/locations').then(r => setLocations(r.data?.locations || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!target) {
      toast.error('Vyberte cílovou lokaci.');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/instruments/${inst.id}/move-to`, { location: target, note });
      toast.success(`Přístroj přesunut do: ${target}`);
      setShowForm(false);
      setTarget('');
      setNote('');
      onRefresh?.();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const history = inst.locationHistory || [];

  return (
    <section>
      <h3 className="ct-eyebrow mb-2 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Lokace měřidla
      </h3>
      <div className="ct-panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="ct-eyebrow mr-2">Aktuální lokace:</span>
            {inst.currentLocation ? (
              <span className="font-medium" style={{ color: 'var(--ct-accent)' }}>{inst.currentLocation}</span>
            ) : (
              <span className="text-xs italic" style={{ color: 'var(--ct-text-disabled)' }}>(neznámá)</span>
            )}
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="ct-btn-secondary gap-1 text-xs h-7">
              <Move className="w-3 h-3" /> Přesunout
            </Button>
          )}
        </div>

        {showForm && (
          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--ct-border)' }}>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={target}
                onChange={e => setTarget(e.target.value)}
                className="ct-input text-sm"
              >
                <option value="">— Vyberte cílovou lokaci —</option>
                {locations.filter(l => l !== inst.currentLocation).map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <Input
                placeholder="Poznámka (volitelné)"
                value={note}
                onChange={e => setNote(e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={submit} disabled={busy || !target} className="ct-btn-primary gap-1 text-xs">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Move className="w-3 h-3" />}
                Přesunout
              </Button>
              <Button onClick={() => setShowForm(false)} variant="outline" className="text-xs">
                Zrušit
              </Button>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer" style={{ color: 'var(--ct-text-secondary)' }}>
              Historie přesunů ({history.length})
            </summary>
            <ul className="mt-2 space-y-1">
              {history.slice().reverse().map((h, i) => (
                <li key={i} style={{ color: 'var(--ct-text-secondary)' }}>
                  <span className="font-mono">{new Date(h.movedAt).toLocaleString('cs-CZ')}</span>
                  · {h.fromLocation || '—'} → <span style={{ color: 'var(--ct-text)' }}>{h.toLocation}</span>
                  · {h.movedByName}
                  {h.note && <span className="italic"> · „{h.note}"</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

function OverviewTab({ inst, companies, onRefresh }) {
  const companyName = (id) => {
    if (!id) return '—';
    const c = companies.find(x => x.id === id);
    return c?.name || id;
  };
  const [generatingBarcode, setGeneratingBarcode] = useState(false);
  const generateBarcode = async () => {
    setGeneratingBarcode(true);
    try {
      const res = await api.post(`/instruments/${inst.id}/generate-barcode`);
      toast.success(`Barcode přiřazen: ${res.data.barcode}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toastApiError(err, 'Chyba při generování barcode');
    } finally {
      setGeneratingBarcode(false);
    }
  };
  return (
    <div className="space-y-6">
      <section>
        <h3 className="ct-eyebrow mb-2">Identifikace</h3>
        <div className="ct-panel p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="ct-eyebrow">Název</div>
              <div style={{ color: 'var(--ct-text)' }}>{inst.name || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">Typ</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>{inst.type || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">Výrobní číslo</div>
              <div className="font-mono text-xs" style={{ color: 'var(--ct-text)' }}>{inst.serialNumber || '—'}</div>
            </div>
            <div>
              <div className="ct-eyebrow">Výrobce</div>
              <div style={{ color: 'var(--ct-text)' }}>{companyName(inst.manufacturer)}</div>
            </div>
            <div>
              <div className="ct-eyebrow">Dodavatel</div>
              <div style={{ color: 'var(--ct-text)' }}>{companyName(inst.supplier)}</div>
            </div>
            <div>
              <div className="ct-eyebrow">Provozovatel</div>
              <div style={{ color: 'var(--ct-text)' }}>{companyName(inst.operator)}</div>
            </div>
            {/* Čárový kód */}
            <div className="col-span-2 pt-2 mt-2" style={{ borderTop: '1px solid var(--ct-border)' }}>
              <div className="ct-eyebrow flex items-center gap-1">
                <Barcode className="w-3 h-3" /> Čárový kód
              </div>
              <div className="flex items-center gap-3 mt-1">
                {inst.barcode ? (
                  <span className="font-mono text-base" style={{ color: 'var(--ct-accent)' }}>
                    {inst.barcode}
                  </span>
                ) : (
                  <span className="text-xs italic" style={{ color: 'var(--ct-text-disabled)' }}>
                    Zatím nepřiřazený — klikněte „Generovat" pro přiřazení.
                  </span>
                )}
                {!inst.barcode && (
                  <Button
                    onClick={generateBarcode}
                    disabled={generatingBarcode}
                    className="ct-btn-primary gap-1 text-xs h-7"
                  >
                    {generatingBarcode ? <Loader2 className="w-3 h-3 animate-spin" /> : <Barcode className="w-3 h-3" />}
                    Generovat
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lokace měřidla */}
      <LocationSection inst={inst} onRefresh={onRefresh} />

      {inst.notes && (
        <section>
          <h3 className="ct-eyebrow mb-2">Poznámka</h3>
          <div className="ct-panel p-4 text-sm" style={{ color: 'var(--ct-text)', whiteSpace: 'pre-wrap' }}>
            {inst.notes}
          </div>
        </section>
      )}
    </div>
  );
}

function ProbesTab({ probes, units, probeTypes, methodologies, onAdd, onEdit, onDelete }) {
  const unitSymbol = (id) => units.find(u => u.id === id)?.symbol || '—';
  const probeTypeName = (id) => probeTypes.find(pt => pt.id === id)?.name || '—';
  const methCode = (id) => {
    const m = methodologies.find(x => x.id === id);
    return m ? `${m.code || ''}${m.title ? ` — ${m.title}` : ''}` : '—';
  };
  const readLabel = (r) => (r === 'digital' ? 'Digitální' : r === 'analog' ? 'Analogový' : '—');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
          {probes.length === 0
            ? 'Přístroj zatím nemá žádné sondy.'
            : `Sondy přístroje (${probes.length}) — měřicí kanály s vlastní metodikou a rozsahem.`}
        </p>
        <Button onClick={onAdd} className="ct-btn-primary gap-1 text-xs">
          <Plus className="w-3.5 h-3.5" /> Přidat sondu
        </Button>
      </div>

      {probes.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="Žádné sondy"
          description="Přidejte sondu — měřicí kanál s metodikou a rozsahem. Bez sond nelze tento přístroj kalibrovat."
        />
      ) : (
        <div className="ct-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                <th className="text-left py-2 px-3 ct-eyebrow">Kód</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Název</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Typ</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Odečet</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Rozsah</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Metodika</th>
                <th className="w-20 ct-eyebrow"></th>
              </tr>
            </thead>
            <tbody>
              {probes.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--ct-border)' }}>
                  <td className="py-2 px-3 font-mono text-xs" style={{ color: 'var(--ct-accent)' }}>{p.code}</td>
                  <td className="py-2 px-3">{p.name}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                    {probeTypeName(p.probeTypeId)}
                  </td>
                  <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                    {readLabel(p.probeRead)}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">
                    {p.rangeMin}–{p.rangeMax} {unitSymbol(p.unitId)}
                  </td>
                  <td className="py-2 px-3 text-xs font-mono">{methCode(p.methodologyId)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => onEdit(p)}
                        title="Upravit sondu"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-text-secondary)' }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(p)}
                        title="Smazat sondu"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-error)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ calibrations }) {
  if (calibrations.length === 0) {
    return (
      <EmptyState
        icon={FlaskConical}
        title="Žádné kalibrace"
        description="Tento přístroj zatím nebyl kalibrován. Vytvoří se přes Evidence kalibrací (výběr z příjmu)."
      />
    );
  }

  return (
    <div className="ct-panel overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: 'var(--ct-bg-secondary)' }}>
            <th className="text-left py-2 px-3 ct-eyebrow">Datum</th>
            <th className="text-left py-2 px-3 ct-eyebrow">Certifikát</th>
            <th className="text-left py-2 px-3 ct-eyebrow">Laboratoř</th>
            <th className="text-left py-2 px-3 ct-eyebrow">Metodika</th>
            <th className="text-left py-2 px-3 ct-eyebrow">Stav</th>
            <th className="text-left py-2 px-3 ct-eyebrow">Výsledek</th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody>
          {calibrations.map((c) => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--ct-border)' }}>
              <td className="py-2 px-3 font-mono text-xs">
                {c.createdAt ? new Date(c.createdAt).toLocaleDateString('cs-CZ') : '—'}
              </td>
              <td className="py-2 px-3 font-mono text-xs">
                {c.certificateNumber
                  ? <span style={{ color: 'var(--ct-accent)' }}>{c.certificateNumber}</span>
                  : <span style={{ color: 'var(--ct-text-disabled)' }}>—</span>}
              </td>
              <td className="py-2 px-3">{c.laboratory || '—'}</td>
              <td className="py-2 px-3 font-mono text-xs">{c.methodologyCode || '—'}</td>
              <td className="py-2 px-3"><StatusBadge status={c.status} /></td>
              <td className="py-2 px-3">{c.result ? <StatusBadge status={c.result} /> : '—'}</td>
              <td className="py-2 px-3">
                <Link
                  to={`/calibrations/${c.id}`}
                  className="text-xs font-mono px-1.5 py-0.5 rounded transition-opacity hover:opacity-80"
                  style={{ color: 'var(--ct-accent)' }}
                  title="Otevřít detail kalibrace"
                >
                  →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RelatedTab({ orders }) {
  if (orders.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="Žádné objednávky"
        description="Tento přístroj zatím nefiguroval v žádné objednávce."
      />
    );
  }

  return (
    <section>
      <h3 className="ct-eyebrow mb-2">Objednávky s tímto přístrojem ({orders.length})</h3>
      <div className="ct-panel overflow-hidden">
        {orders.map((o, idx) => (
          <div
            key={o.id}
            className="px-4 py-2.5 text-sm flex items-center justify-between gap-3"
            style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--ct-border)' }}
          >
            <div className="min-w-0 flex items-center gap-3 flex-1">
              <RefLink type="order" id={o.orderNumber} />
              <span className="text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
                {o.customer?.company || 'Bez klienta'}
              </span>
            </div>
            <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--ct-text-disabled)' }}>
              {o.createdAt ? new Date(o.createdAt).toLocaleDateString('cs-CZ') : ''}
            </span>
            <StatusBadge status={o.status} className="flex-shrink-0" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function InstrumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [calibrations, setCalibrations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  // Probes state + dialog management
  const [probes, setProbes] = useState([]);
  const [units, setUnits] = useState([]);
  const [probeTypes, setProbeTypes] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [probeDialogOpen, setProbeDialogOpen] = useState(false);
  const [editingProbe, setEditingProbe] = useState(null);

  const refetchProbes = useCallback(() => {
    api.get(`/probes?instrumentId=${id}`)
      .then(r => setProbes(r.data))
      .catch(() => setProbes([]));
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    api.get(`/instruments/${id}`)
      .then(r => {
        if (cancelled) return;
        setInst(r.data);
        // Historie kalibrací — match přes name+type (stejný pattern jako v listu).
        api.get('/calibrations').then(cr => {
          if (cancelled) return;
          const matched = cr.data
            .filter(c => c.instrumentName === r.data.name && c.instrumentType === r.data.type)
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setCalibrations(matched);
        }).catch(() => {});
        // Related orders — match přes name+type v instruments[] poli (fuzzy).
        api.get('/orders').then(or => {
          if (cancelled) return;
          const matched = or.data
            .filter(o => (o.instruments || []).some(i =>
              i.name === r.data.name && i.type === r.data.type
            ))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setOrders(matched);
        }).catch(() => {});
      })
      .catch(err => { if (!cancelled) setNotFound(err.response?.status === 404); })
      .finally(() => { if (!cancelled) setLoading(false); });

    api.get('/companies').then(r => { if (!cancelled) setCompanies(r.data); }).catch(() => {});

    // Probes + master data pro probe tabulku.
    api.get(`/probes?instrumentId=${id}`).then(r => { if (!cancelled) setProbes(r.data); }).catch(() => {});
    api.get('/units').then(r => { if (!cancelled) setUnits(r.data); }).catch(() => {});
    api.get('/probe-types').then(r => { if (!cancelled) setProbeTypes(r.data); }).catch(() => {});
    api.get('/methodologies').then(r => { if (!cancelled) setMethodologies(r.data); }).catch(() => {});

    return () => { cancelled = true; };
  }, [id]);

  const handleAddProbe = () => { setEditingProbe(null); setProbeDialogOpen(true); };
  const handleEditProbe = (probe) => { setEditingProbe(probe); setProbeDialogOpen(true); };
  const handleDeleteProbe = async (probe) => {
    if (!window.confirm(`Smazat sondu "${probe.code} — ${probe.name}"? Kalibrace navázané na sondu zablokují smazání.`)) return;
    try {
      await api.delete(`/probes/${probe.id}`);
      toast.success('Sonda smazána');
      refetchProbes();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat sondu');
    }
  };

  if (loading) return <LoadingState message="Načítám přístroj…" />;
  if (notFound || !inst) {
    return (
      <EmptyState
        icon={Gauge}
        title="Přístroj nenalezen"
        description="Tento přístroj neexistuje nebo byl smazán."
        action={<Button onClick={() => navigate('/instruments')} className="ct-btn-secondary">Zpět na seznam</Button>}
      />
    );
  }

  return (
    <div data-testid="instrument-detail-page" className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/instruments')}
            className="flex items-center gap-1 text-sm transition-colors hover:opacity-80"
            style={{ color: 'var(--ct-text-secondary)' }}
            title="Zpět na seznam přístrojů"
          >
            <ArrowLeft className="w-4 h-4" />
            Přístroje
          </button>
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="ct-h1 truncate" style={{ color: 'var(--ct-accent)' }}>
              {inst.name || '—'}
            </h1>
            <span className="font-mono text-sm truncate" style={{ color: 'var(--ct-text-secondary)' }}>
              {inst.type || ''}{inst.serialNumber ? ` · v.č. ${inst.serialNumber}` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto" style={{ borderColor: 'var(--ct-border)' }}>
        {TABS.map(t => (
          <TabButton
            key={t.id}
            tab={t}
            active={activeTab === t.id}
            onClick={() => setActiveTab(t.id)}
          />
        ))}
      </div>

      {/* Trvalé upozornění: bez sond nelze přístroj kalibrovat (send-to-calibration
          by pro něj nevytvořil žádnou kalibraci). */}
      {probes.length === 0 && (
        <div
          className="flex items-start gap-2 mt-3 px-3 py-2 rounded text-sm"
          style={{ background: 'var(--ct-warning-bg, rgba(234,179,8,0.12))', border: '1px solid var(--ct-warning)', color: 'var(--ct-text)' }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--ct-warning)' }} />
          <span>
            Tento přístroj nemá žádnou sondu — <strong>nelze jej kalibrovat</strong>.
            Přidejte měřicí kanál v záložce <button
              type="button"
              onClick={() => setActiveTab('probes')}
              className="underline font-medium"
            >Sondy</button> (sonda musí mít metodiku).
          </span>
        </div>
      )}

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab
            inst={inst}
            companies={companies}
            onRefresh={() => api.get(`/instruments/${id}`).then(r => setInst(r.data)).catch(() => {})}
          />
        )}
        {activeTab === 'probes' && (
          <ProbesTab
            probes={probes}
            units={units}
            probeTypes={probeTypes}
            methodologies={methodologies}
            onAdd={handleAddProbe}
            onEdit={handleEditProbe}
            onDelete={handleDeleteProbe}
          />
        )}
        {activeTab === 'datasheet' && (
          <DatasheetPanel
            entityKind="instruments"
            entityId={id}
            initialDatasheet={inst.datasheet}
            onVerified={() => api.get(`/instruments/${id}`).then(r => setInst(r.data)).catch(() => {})}
          />
        )}
        {activeTab === 'history' && <HistoryTab calibrations={calibrations} />}
        {activeTab === 'related' && <RelatedTab orders={orders} />}
      </div>

      <ProbeFormDialog
        open={probeDialogOpen}
        onClose={() => setProbeDialogOpen(false)}
        instrumentId={id}
        probe={editingProbe}
        onSaved={refetchProbes}
      />
    </div>
  );
}
